import React, { useEffect, useRef, useState } from "react";
import { getOtp1, ValidateOtp } from "../services/Auth";
import {
  getLuckyDrawWinnerList,
  claimLuckyDrawWinner,
} from "../services/apiService";
import { Link } from "react-router-dom";

const WinnerList = () => {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(false);

  // OTP states
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);

  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");

  // OTP timer - 5 minutes
  const [timer, setTimer] = useState(0);

  // =========================
  // FETCH WINNER LIST
  // =========================
  const fetchWinnerList = async () => {
    try {
      setLoading(true);

      const response = await getLuckyDrawWinnerList();

      console.log("WINNER RESPONSE:", response);

      setWinners(response.list || []);
    } catch (error) {
      console.error("WINNER API ERROR:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWinnerList();
  }, []);

  // =========================
  // OTP TIMER
  // =========================
  useEffect(() => {
    if (timer <= 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  // =========================
  // CLAIM BUTTON
  // =========================
  const handleClaim = async (winner) => {
    try {
      setSelectedWinner(winner);
      setOtpError("");
      setOtp(["", "", "", "", "", ""]);

      console.log("Sending OTP to:", winner.mobileno);

      const otpRes = await getOtp1({
        mobile: winner.mobileno,
        type: "luckydraw",
      });

      console.log("OTP RESPONSE:", otpRes);

      if (otpRes.status === 200) {
        setShowOtpModal(true);

        // Start 5 minute timer
        setTimer(300);

        setTimeout(() => {
          if (otpRefs.current[0]) {
            otpRefs.current[0].focus();
          }
        }, 100);
      } else {
        setOtpError("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      console.error("OTP ERROR:", error);
      console.error("OTP ERROR DATA:", error?.response?.data);

      setOtpError("Failed to send OTP. Please try again.");
    }
  };

  // =========================
  // RESEND OTP
  // =========================
  const resendOtp = async () => {
    if (timer > 0 || !selectedWinner) return;

    setOtpLoading(true);
    setOtpError("");

    try {
      console.log("Resending OTP to:", selectedWinner.mobileno);

      const res = await getOtp1({
        mobile: selectedWinner.mobileno,
        type: "luckydraw",
      });

      console.log("RESEND OTP RESPONSE:", res);

      if (res.status === 200) {
        // Restart 5 minute timer
        setTimer(300);

        // Clear old OTP
        setOtp(["", "", "", "", "", ""]);

        setOtpError("");

        // Focus first OTP input
        setTimeout(() => {
          if (otpRefs.current[0]) {
            otpRefs.current[0].focus();
          }
        }, 100);
      } else {
        setOtpError("Failed to resend OTP");
      }
    } catch (error) {
      console.error("RESEND OTP ERROR:", error);
      console.error("RESEND OTP ERROR DATA:", error?.response?.data);

      setOtpError("Failed to resend OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  // =========================
  // VERIFY OTP + CLAIM
  // =========================
  const verifyWinnerOtp = async () => {
    const code = otp.join("");

    if (code.length !== 6) {
      setOtpError("Enter 6 digit OTP");
      return;
    }

    setOtpLoading(true);
    setOtpError("");

    try {
      console.log("Verifying OTP:", code);
      console.log("Winner:", selectedWinner);

      const res = await ValidateOtp({
        mobile: selectedWinner.mobileno,
        otp: code,
        type: "luckydraw",
      });

      console.log("VALIDATE OTP RESPONSE:", res);

      if (res.data.Status === "done") {
        // Mark winner as claimed in backend
        const claimRes = await claimLuckyDrawWinner({
          id: selectedWinner.id,
          claim: "1",
        });

        console.log("CLAIM RESPONSE:", claimRes);

        // Refresh winner list
        const updatedResponse = await getLuckyDrawWinnerList();

        console.log("UPDATED WINNER LIST:", updatedResponse);

        setWinners(updatedResponse.list || []);

        // Stop timer
        setTimer(0);

        // Close modal
        setShowOtpModal(false);

        alert("Winner claimed successfully!");
      } else {
        setOtpError("Invalid OTP");
      }
    } catch (error) {
      console.error("VERIFY/CLAIM ERROR:", error);
      console.error("VERIFY ERROR DATA:", error?.response?.data);

      setOtpError("OTP verification failed");
    } finally {
      setOtpLoading(false);
    }
  };

  // =========================
  // OTP INPUT
  // =========================
  const handleOtpChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];

    newOtp[index] = value.slice(-1);

    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  // =========================
  // BACKSPACE
  // =========================
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "50px 40px",
        background: "#f5f7fa",
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "1200px",
          margin: "0 auto",
          background: "#fff",
          borderRadius: "12px",
          padding: "30px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        {/* =========================
            HEADER
        ========================= */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "25px",
          }}
        >
          <div className="logo" style={{ width: "160px" }}>
            <Link to="/" className="logo-link" aria-label="Pixel home">
              <img
                src="https://pixeltools.in/assets/logo-Dm-Kl2OT.png"
                alt="PIXEL"
                className="logo-img"
              />
            </Link>
          </div>
          <h2
            style={{
              margin: 0,
              color: "#005487",
              fontSize: "26px",
              fontWeight: 600,
            }}
          >
            Lucky Draw Winners
          </h2>

          <span
            style={{
              padding: "8px 16px",
              background: "#e8f7f2",
              color: "#009b77",
              borderRadius: "20px",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            {winners.length} Winner
            {winners.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* =========================
            WINNER TABLE
        ========================= */}
        {loading ? (
          <p>Loading winners...</p>
        ) : (
          <div
            style={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <table
              style={{
                width: "100%",
                minWidth: "700px",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#005487",
                  }}
                >
                  {[
                    "ID",
                    "Name",
                    "Mobile Number",
                    "Date",
                    "Status",
                    "Action",
                  ].map((heading) => (
                    <th
                      key={heading}
                      style={{
                        padding: "15px 18px",
                        color: "#fff",
                        textAlign: "left",
                        fontSize: "14px",
                        fontWeight: 600,
                      }}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {winners.length > 0 ? (
                  winners.map((winner) => (
                    <tr key={winner.id}>
                      <td style={tdStyle}>{winner.id}</td>

                      <td
                        style={{
                          ...tdStyle,
                          textTransform: "uppercase",
                          fontWeight: "bold",
                        }}
                      >
                        {winner.name}
                      </td>

                      <td style={tdStyle}>
                        XXXXXX{winner.mobileno?.slice(-4)}
                      </td>

                      <td style={tdStyle}>
                        {new Date(winner.date).toLocaleDateString("en-IN")}
                      </td>

                      {/* STATUS */}
                      <td style={tdStyle}>
                        <span
                          style={{
                            padding: "6px 12px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: 600,

                            background:
                              winner.claim === "1" || winner.claim === 1
                                ? "#d4edda"
                                : "#fff3cd",

                            color:
                              winner.claim === "1" || winner.claim === 1
                                ? "#155724"
                                : "#856404",
                          }}
                        >
                          {winner.claim === "1" || winner.claim === 1
                            ? "Claimed"
                            : "Not Claimed"}
                        </span>
                      </td>

                      {/* ACTION */}
                      <td style={tdStyle}>
                        {winner.claim === null && (
                          <button
                            onClick={() => handleClaim(winner)}
                            style={{
                              background: "#184764",
                              color: "#fff",
                              border: "none",
                              padding: "8px 18px",
                              borderRadius: "6px",
                              fontSize: "13px",
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            Claim
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      style={{
                        padding: "40px",
                        textAlign: "center",
                        color: "#777",
                      }}
                    >
                      No winners found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* =========================
          OTP MODAL
      ========================= */}
      {showOtpModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              width: "400px",
              maxWidth: "90%",
              background: "#fff",
              borderRadius: "16px",
              padding: "30px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
          >
            {/* MODAL HEADER */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  color: "#000",
                  fontSize: "23px",
                  fontWeight: 500,
                }}
              >
                Verify Winner
              </h2>

              <span
                onClick={() => {
                  setShowOtpModal(false);
                  setOtpError("");
                }}
                style={{
                  fontSize: "22px",
                  cursor: "pointer",
                  color: "#666",
                }}
              >
                ✕
              </span>
            </div>

            {/* WINNER DETAILS */}
            {selectedWinner && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "15px",
                  background: "#f5f7fa",
                  borderRadius: "8px",
                  lineHeight: "1.8",
                }}
              >
                <div>
                  <strong>Name:</strong> {selectedWinner.name}
                </div>

                <div>
                  <strong>Mobile:</strong> XXXXXXX
                  {selectedWinner?.mobileno?.slice(-3)}
                </div>
              </div>
            )}

            {/* OTP MESSAGE */}
            <p
              style={{
                textAlign: "center",
                color: "#555",
                marginTop: "20px",
              }}
            >
              OTP sent to mobile number{" "}
              <strong>{selectedWinner?.mobileno}</strong>
            </p>

            {/* ERROR */}
            {otpError && (  
              <p
                style={{
                  color: "#d93025",
                  textAlign: "center",
                  fontSize: "14px",
                }}
              >
                {otpError}
              </p>
            )}

            {/* OTP INPUTS */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                margin: "25px 0 10px",
              }}
            >
              {otp.map((value, index) => (
                <input
                  key={index}
                  ref={(el) => (otpRefs.current[index] = el)}
                  value={value}
                  maxLength={1}
                  inputMode="numeric"
                  onChange={(e) => handleOtpChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  style={{
                    width: "42px",
                    height: "48px",
                    textAlign: "center",
                    fontSize: "20px",
                    border: "1px solid #ccc",
                    borderRadius: "7px",
                    outline: "none",
                  }}
                />
              ))}
            </div>

            {/* =========================
                RESEND OTP
            ========================= */}
            <p
              style={{
                textAlign: "center",
                margin: "5px 0 20px",
                color: "#555",
                fontSize: "14px",
              }}
            >
              {timer > 0 ? (
                <>
                  Resend in{" "}
                  <strong>
                    {Math.floor(timer / 60)}:
                    {(timer % 60).toString().padStart(2, "0")}
                  </strong>
                </>
              ) : (
                <span
                  onClick={resendOtp}
                  style={{
                    color: "#005487",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Resend OTP
                </span>
              )}
            </p>

            {/* VERIFY BUTTON */}
            <button
              onClick={verifyWinnerOtp}
              disabled={otp.join("").length !== 6 || otpLoading}
              style={{
                width: "100%",
                padding: "12px",
                background: otp.join("").length === 6 ? "#005487" : "#aaa",
                color: "#fff",
                border: "none",
                borderRadius: "7px",
                fontSize: "15px",
                fontWeight: 600,
                cursor: otp.join("").length === 6 ? "pointer" : "not-allowed",
              }}
            >
              {otpLoading ? "Verifying..." : "Verify OTP"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const tdStyle = {
  padding: "16px 18px",
  borderBottom: "1px solid #e9ecef",
  color: "#333",
  fontSize: "14px",
};

export default WinnerList;
